import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import { useDeleteChunk, useGetChunks, useGetDataSet } from 'apis/business/setting/knowledgeBase/hook';
import { ChunkResponse } from 'apis/business/setting/knowledgeBase/types';
import Button from 'components/Common/Button';
import DataTable from 'components/Common/DataTable';
import Pagination from 'components/Common/Pagination';
import Search from 'components/Common/Search';
import { useState } from 'react';
import { GoPlus } from 'react-icons/go';
import { MdArrowBack, MdOutlineEdit } from 'react-icons/md';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { Link, Outlet, useNavigate, useParams } from 'react-router-dom';

export default function Chunks() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { datasetName } = useParams();
  const [searchText, setSearchText] = useState<null | string>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const { data: dataset } = useGetDataSet();
  const { data: chunks, isLoading: isChunksLoading } = useGetChunks(searchText, currentPage);
  const { mutateAsync: deleteChunk } = useDeleteChunk();

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}
      >
        <Box display='flex' gap={1} alignItems='center'>
          <IconButton onClick={() => navigate(-1)}>
            <MdArrowBack />
          </IconButton>
          <Typography variant='h6'>{datasetName}</Typography>
        </Box>
        <Box display='flex' gap={3}>
          <Search
            size='small'
            sx={{ background: theme.palette.background.paper }}
            placeholder='Search your files'
            searchText={searchText}
            setSearchText={setSearchText}
          />
          <Button
            onClick={() => navigate('add')}
            variant='contained'
            color='primary'
            startIcon={<GoPlus />}
            sx={{ textTransform: 'none' }}
          >
            Add New
          </Button>
        </Box>
      </Box>
      <DataTable
        data={chunks?.data || []}
        columns={dataset?.datasource_type === 'qa' ? qaColumns(deleteChunk) : columns(deleteChunk)}
        isHeaderSticky={true}
        isLoading={isChunksLoading}
      />
      <Pagination totalPages={chunks?.meta?.total_pages} currentPage={currentPage} onChange={setCurrentPage} />
      <Outlet />
    </>
  );
}

type DeleteFn = (id: string) => void;

const columns = (deleteChunk: DeleteFn): ColumnDef<ChunkResponse>[] => [
  {
    header: 'Chunk',
    accessorKey: 'content_with_weight',
    cell: (info) => (
      // dangerouslySetInnerHTML={{ __html: info.getValue() as string }}
      <Typography fontSize={16} sx={{ wordBreak: 'break-word' }}>
        {info.getValue() as string}
      </Typography>
    )
  },
  {
    header: 'Action',
    cell: (info) => (
      <Box display='flex' gap={1} alignItems='center'>
        <Link to={`${info.row.original.chunk_id}/edit`}>
          <IconButton>
            <MdOutlineEdit />
          </IconButton>
        </Link>
        <IconButton onClick={() => deleteChunk(info.row.original.chunk_id)} color='error'>
          <RiDeleteBin6Line style={{ color: 'red' }} />
        </IconButton>
      </Box>
    )
  }
];

const qaColumns = (deleteChunk: DeleteFn): ColumnDef<ChunkResponse>[] => [
  {
    header: 'Question',
    accessorKey: 'question'
  },
  {
    header: 'Answer',
    accessorKey: 'answer',
    cell: (info) => (
      <Typography fontSize={16} sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {info.getValue() as string}
      </Typography>
    )
  },
  {
    header: 'Action',
    cell: (info) => (
      <Box display='flex' gap={1} alignItems='center'>
        <Link to={`${info.row.original.chunk_id}/edit`}>
          <IconButton>
            <MdOutlineEdit />
          </IconButton>
        </Link>
        <IconButton onClick={() => deleteChunk(info.row.original.chunk_id)} color='error'>
          <RiDeleteBin6Line style={{ color: 'red' }} />
        </IconButton>
      </Box>
    )
  }
];
